import INVENTORY_API_BASE_URL from "../../config/api";
import { useState, useEffect } from "react";
import "./DistributorAudit.css";
import Axios from "axios";
import Table from "react-bootstrap/Table";

const DistributorAudit = () => {
  const [data, setData] = useState([]);
  const [distDetails, setDistDetails] = useState([]);
  const [distributorName, setDistributorName] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalSkuCount, setTotalSkuCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [totalQuantityForSearchedSku, setTotalQuantityForSearchedSku] =
    useState(0);
  const [reverseOrder] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { day: "numeric", month: "long", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  useEffect(() => {
    const fetchData = async () => {
      const response = await Axios.get(
        `${INVENTORY_API_BASE_URL}/getApproveDPO`
      );
      const reversedData = reverseOrder
        ? response.data.reverse()
        : response.data;
      setData(reversedData);
    };

    fetchData();
  }, [reverseOrder]);

  useEffect(() => {
    Axios.get(
      `${INVENTORY_API_BASE_URL}/getCreatesuperdistributor`
    ).then((respo) => {
      setDistDetails(respo.data);
    });
  }, []);

  useEffect(() => {
    const filteredItems = data.filter((item) => {
      const updatedQuantity = item.updatedQuantity || [];
      return updatedQuantity.some(
        (quantityItem) => quantityItem.distributorName === distributorName
      );
    });
    setFilteredData(filteredItems);

    const total = filteredItems.reduce((acc, item) => {
      const skuCount = item.updatedQuantity.filter(
        (quantityItem) => quantityItem.sku === searchQuery
      ).length;
      return acc + skuCount;
    }, 0);

    setTotalSkuCount(total);
    setTotalRowCount(filteredItems.length);
  }, [data, distributorName, searchQuery]);

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    setDistributorName(selectedValue);
    setFilteredData([]);
  };

  const filteredDataBySku = filteredData.filter((item) => {
    const hasSkuOrTitle = item.updatedQuantity.some(
      (quantityItem) =>
        quantityItem.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
        quantityItem.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const itemDate = new Date(item.ApprovalDate);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const inDateRange = (!from || itemDate >= from) && (!to || itemDate <= to);

    return hasSkuOrTitle && inDateRange;
  });

  useEffect(() => {
    const totalQuantity = filteredDataBySku
      .flatMap((item) => item.updatedQuantity)
      .filter(
        (quantityItem) =>
          quantityItem.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
          quantityItem.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .reduce((acc, quantityItem) => acc + quantityItem.approvedquantity, 0);
    setTotalQuantityForSearchedSku(totalQuantity);
  }, [filteredDataBySku, searchQuery]);

  return (
    <main className="distributor-audit">
      <header className="distributor-audit__header">
        <div>
          <p className="distributor-audit__eyebrow">AUDIT &amp; COMPLIANCE</p>
          <h1>Distributor audit</h1>
          <p className="distributor-audit__subtitle">
            Review approved orders and quantities by distributor, product, and date.
          </p>
        </div>
        <span className="distributor-audit__source">
          <span className="distributor-audit__source-dot" aria-hidden="true" />
          Approved DPO records
        </span>
      </header>

      <section className="distributor-audit__filter-panel" aria-label="Audit filters">
        <div className="distributor-audit__panel-heading">
          <div>
            <h2>Find audit records</h2>
            <p>Select a distributor, then refine the results by product or approval date.</p>
          </div>
        </div>
        <div className="distributor-audit__filters">
          <div className="distributor-audit__field distributor-audit__field--distributor">
            <label htmlFor="audit-distributor">Distributor</label>
            <select
              id="audit-distributor"
              value={distributorName}
              onChange={handleSelectChange}
            >
              <option value="">Select distributor</option>
              {distDetails.map((item) => (
                <option key={item.id} value={item.dname}>
                  {item.dname}
                </option>
              ))}
            </select>
          </div>
          <div className="distributor-audit__field distributor-audit__field--search">
            <label htmlFor="audit-search">SKU or title</label>
            <div className="distributor-audit__search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="10.8" cy="10.8" r="6.8" />
                <path d="m16 16 4.5 4.5" />
              </svg>
              <input
                id="audit-search"
                className="searchaudit"
                type="search"
                placeholder="Search SKU or book title"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="distributor-audit__field">
            <label htmlFor="audit-from-date">From date</label>
            <input
              id="audit-from-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="distributor-audit__field">
            <label htmlFor="audit-to-date">To date</label>
            <input
              id="audit-to-date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="distributor-audit__metrics" aria-label="Audit summary">
        <article className="distributor-audit__metric distributor-audit__metric--blue">
          <span className="distributor-audit__metric-icon" aria-hidden="true">SKU</span>
          <div>
            <span className="distributor-audit__metric-label">Total SKU count</span>
            <strong>{totalSkuCount.toLocaleString()}</strong>
          </div>
        </article>
        <article className="distributor-audit__metric distributor-audit__metric--violet">
          <span className="distributor-audit__metric-icon" aria-hidden="true">ORD</span>
          <div>
            <span className="distributor-audit__metric-label">Total orders</span>
            <strong>{totalRowCount.toLocaleString()}</strong>
          </div>
        </article>
        <article className="distributor-audit__metric distributor-audit__metric--green">
          <span className="distributor-audit__metric-icon" aria-hidden="true">QTY</span>
          <div>
            <span className="distributor-audit__metric-label">Total sell quantity · SKU wise</span>
            <strong>{totalQuantityForSearchedSku.toLocaleString()}</strong>
          </div>
        </article>
      </section>

      <section className="distributor-audit__results" aria-label="Distributor audit results">
        <div className="distributor-audit__results-heading">
          <div>
            <h2>Approved order history</h2>
            <p>
              {distributorName
                ? "Audit lines for " + distributorName
                : "Choose a distributor to view its approved orders."}
            </p>
          </div>
          <span className="distributor-audit__record-count">
            {filteredDataBySku.length.toLocaleString()} orders
          </span>
        </div>

        {!distributorName ? (
          <div className="distributor-audit__empty">
            <span className="distributor-audit__empty-icon" aria-hidden="true">DA</span>
            <strong>Select a distributor to begin</strong>
            <p>Use the distributor selector above to load its audit history.</p>
          </div>
        ) : filteredDataBySku.length === 0 ? (
          <div className="distributor-audit__empty">
            <span className="distributor-audit__empty-icon" aria-hidden="true">⌕</span>
            <strong>No matching audit records</strong>
            <p>Try changing the search text or date range.</p>
          </div>
        ) : (
          <div className="distributor-audit__table-wrap">
            <Table className="distributor-audit__table" hover>
              <thead>
                <tr>
                  <th scope="col">Order ID</th>
                  <th scope="col">Approval date</th>
                  <th scope="col">Approved items</th>
                </tr>
              </thead>
              <tbody>
                {filteredDataBySku.map((item) => (
                  <tr key={item.shipmentid}>
                    <td data-label="Order ID">
                      <span className="distributor-audit__order-label">ORDER</span>
                      <strong className="distributor-audit__order-id">{item.shipmentid}</strong>
                    </td>
                    <td data-label="Approval date">
                      <span className="distributor-audit__date">
                        {formatDate(item.ApprovalDate)}
                      </span>
                    </td>
                    <td data-label="Approved items">
                      <div className="distributor-audit__items">
                        {item.updatedQuantity.map((quantityItem, index) => (
                          <div
                            className="distributor-audit__item"
                            key={quantityItem.sku + "-" + index}
                          >
                            <strong className="distributor-audit__sku">{quantityItem.sku}</strong>
                            <span className="distributor-audit__item-title">{quantityItem.title}</span>
                            <span className="distributor-audit__medium">{quantityItem.Medium || "—"}</span>
                            <span className="distributor-audit__quantity">
                              <strong>{quantityItem.approvedquantity}</strong>
                              <small> approved</small>
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <footer className="distributor-audit__footer">
          <span>Data source: approved distributor purchase orders</span>
          <span>{filteredDataBySku.length.toLocaleString()} record{filteredDataBySku.length === 1 ? "" : "s"}</span>
        </footer>
      </section>
    </main>
  );
};

export default DistributorAudit;
